import { createElement } from 'react';
import styled from 'styled-components';

import * as LayoutUtils from './utils';
import type { Text, Container, LayoutPanels, BaseContainer, SignupForm } from './utils';
import { txt } from '../../../../locales/i18n';

export const signupLayoutPanels: LayoutPanels = {
	layoutName: txt('cms.layoutNames.signup'),
	panels: [
		{
			label: txt('cms.panelNames.layout container'),
			formKey: 'container',
			type: 'baseContainerPanel',
		},

		{
			label: txt('cms.panelNames.text container'),
			formKey: 'textContainer',
			type: 'containerPanel',
		},

		{
			label: txt('cms.panelNames.title'),
			formKey: 'title',
			type: 'textPanel',
		},

		{
			label: txt('cms.panelNames.paragraph'),
			formKey: 'paragraph',
			type: 'textPanel',
		},

		{
			label: txt('cms.panelNames.signup form'),
			formKey: 'signupForm',
			type: 'signupForm',
		},
	],
};

type SignupLayoutProps = {
	container: BaseContainer;
	textContainer: Container;
	title: Text;
	paragraph: Text;
	signupForm: SignupForm;
};

export const signupLayoutValues: SignupLayoutProps = {
	container: LayoutUtils.defaultBaseContainer,
	textContainer: LayoutUtils.defaultContainer,

	title: {
		color: '#2a6592ff',
		text: 'Blev du inspireret?',
		isBold: false,
		isItalic: false,
		isUnderlined: false,
		textAlign: 'center',
		fontFamily: 'Raleway',
		htmlTag: 'h2',
		animation: LayoutUtils.defaultAnimation,
	},

	paragraph: {
		color: '#000000ff',
		text: 'Opret dig og skab dine egne fælleskaber. Boblberg er et trygt og sikkert medie, hvor dine oplysninger er beskyttet. \nHvad venter du på? Gør som {userCount} andre, og opret dig gratis i dag.',
		isBold: false,
		isItalic: false,
		isUnderlined: false,
		textAlign: 'center',
		fontFamily: 'Raleway',
		htmlTag: 'p',
		animation: LayoutUtils.defaultAnimation,
	},

	signupForm: LayoutUtils.defaultSignupForm,
};

export const SignupLayout = (props: SignupLayoutProps) => {
	const header = createElement(props.title.htmlTag, {
		className: 'header',
		dangerouslySetInnerHTML: { __html: LayoutUtils.formatText(props.title.text) },
		...LayoutUtils.getAnimationProps(props.title.animation),
	});

	const paragraph = createElement(props.paragraph.htmlTag, {
		className: 'paragraph',
		dangerouslySetInnerHTML: {
			__html: LayoutUtils.formatText(
				props.paragraph.text.replace('{userCount}', '<b>123.456</b>')
			),
		},
		...LayoutUtils.getAnimationProps(props.paragraph.animation),
	});

	return (
		<SignupLayoutStyled
			styledProps={props}
			{...LayoutUtils.getAnimationProps(props.container.animation)}
		>
			<div
				className="text-container"
				{...LayoutUtils.getAnimationProps(props.textContainer.animation)}
			>
				{props.title.text !== '' && header}
				{props.paragraph.text !== '' && paragraph}
			</div>

			<div className="form-container">
				<form>
					<h3>{props.signupForm.titleText || 'Opret dig gratis her'}</h3>

					<div className="inputs-container">
						<div className="form-row-container">
							<input placeholder="lorem" />
						</div>

						<div className="municipality-selector form-row-container">
							<select>
								<option>hvidøvre</option>
							</select>

							<select>
								<option>dk</option>
								<option>se</option>
								<option>de</option>
							</select>
						</div>

						<div className="form-row-container">
							<input placeholder="lorem" />
						</div>

						<div className="form-row-container">
							<input placeholder="lorem" />
						</div>

						<div className="form-row-container">
							<input placeholder="lorem" />
						</div>
					</div>

					<div className="terms-and-conditions-container">
						<input type="checkbox" />

						<p>
							Jeg accepterer Boblbergs <a href="?">privatlivspolitik</a> og{' '}
							<a href="?">betingelser</a>
						</p>
					</div>

					<button type="button">{props.signupForm.buttonText || 'Bliv medlem'}</button>
				</form>
			</div>
		</SignupLayoutStyled>
	);
};

const SignupLayoutStyled = styled.div<{ styledProps: SignupLayoutProps }>`
	display: flex;
	padding: 2rem;
	align-items: center;
	justify-content: center;

	background-repeat: no-repeat;
	background-color: ${({ styledProps }) => styledProps.container.backgroundColor};
	background-size: ${({ styledProps }) => styledProps?.container?.coverFit ?? 'cover'};
	background-position: ${({ styledProps }) => styledProps?.container?.coverPosition ?? 'center'};
	background-image: ${({ styledProps }) => {
		if (styledProps.container.coverSource === '') return 'none';
		return `url(${LayoutUtils.getImageSource(styledProps.container.coverSource)})`;
	}};
	min-height: ${({ styledProps }) => (styledProps.container.fullscreen ? '100dvh' : 'auto')};

	.text-container {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 3rem;
		width: auto;
		gap: 1rem;
		margin: 2rem;

		${({ styledProps }) => LayoutUtils.getContainerStyle(styledProps.textContainer)};

		.header {
			${({ styledProps }) => LayoutUtils.getTextStyle(styledProps.title)};
		}

		.paragraph {
			${({ styledProps }) => LayoutUtils.getTextStyle(styledProps.paragraph)};
		}
	}

	.form-container {
		flex: 1;
		isolation: isolate;

		* {
			color: ${({ styledProps }) => styledProps.signupForm.textColor};
		}

		a {
			color: ${({ styledProps }) => styledProps.signupForm.linkTextColor};
		}

		form {
			margin-inline: auto;
			position: relative;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 1rem;

			width: 450px;
			aspect-ratio: 1;
			margin: 4rem;

			&::before {
				content: '';
				position: absolute;
				height: 100%;
				width: 100%;
				border-radius: 50%;
				z-index: -1;

				${({ styledProps }) => {
					if (styledProps.signupForm.backgroundGradient !== '') {
						const opacity = Number(styledProps.signupForm.backgroundOpacity) / 255;

						return `
							opacity: ${opacity};
							background: ${styledProps.signupForm.backgroundColor};
							backdrop-filter: blur(${styledProps.signupForm.backgroundBlur}px);
							${styledProps.signupForm.backgroundGradient};
						`;
					}

					const backgroundOpacity = Number(
						styledProps.signupForm.backgroundOpacity
					).toString(16);

					return `
						backdrop-filter: blur(${styledProps.signupForm.backgroundBlur}px);
						background-color: ${
							styledProps.signupForm.backgroundColor +
							(backgroundOpacity === '0' ? '00' : backgroundOpacity)
						};
					`;
				}}
			}

			.inputs-container {
				display: flex;
				flex-direction: column;
				gap: 0.4rem;
			}

			h3 {
				color: ${({ styledProps }) => styledProps.signupForm.headerTextColor};
				text-align: center;
			}

			input {
				font-family: inherit;
				font-size: 16px;
				text-decoration: none;
				border-radius: 12px;
				padding: 0.5em 1em;
				margin-top: 6px;
				border: none;

				color: ${({ styledProps }) => styledProps.signupForm.inputTextColor};
				background-color: ${({ styledProps }) =>
					styledProps.signupForm.inputBackgroundColor};

				&::placeholder {
					color: ${({ styledProps }) => styledProps.signupForm.placeholderTextColor};
					opacity: 1;
				}

				&::-ms-input-placeholder {
					color: ${({ styledProps }) => styledProps.signupForm.placeholderTextColor};
				}
			}

			select {
				font-family: inherit;
				font-size: 16px;
				text-decoration: none;
				border-radius: 12px;
				padding: 0.5em 1em;
				margin-top: 6px;
				border: none;
				height: 36px;

				color: ${({ styledProps }) => styledProps.signupForm.inputTextColor};
				background-color: ${({ styledProps }) =>
					styledProps.signupForm.inputBackgroundColor};
			}

			button {
				background-color: ${({ styledProps }) =>
					styledProps.signupForm.buttonBackgroundColor};
				color: ${({ styledProps }) => styledProps.signupForm.buttonTextColor};
				font-size: 16px;
				font-weight: bold;
				padding: 0.5em 1em;

				border-radius: 25px;
				font-family: Raleway, sans-serif;
				border: none;
				cursor: pointer;
			}

			.municipality-selector {
				display: flex;
				align-items: center;
				justify-content: space-between;
				gap: 1ch;

				select:first-child {
					flex: 1;
				}

				select:last-child {
					all: unset;
				}
			}

			.terms-and-conditions-container {
				display: flex;
				gap: 1rem;
				width: 60%;
			}
		}

		.form-row-container {
			width: 265px;

			input {
				width: 100%;
			}
		}
	}
`;
